import React from "react";
import { connect } from 'react-redux';
import { AppServices } from "../../../AppServices";
import { ResultType } from "../../../store/Models/Api/ApiResponse";
import { ToxnotServiceClient } from "../../../store/ToxnotServiceClient";
import { ToxnotComponentProperties } from "../../core/BaseApplicationComponents/ToxnotComponent";
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from "../../core/ToxnotReactModal";
import * as LoginStateController from '../../../store/StateControllers/LoginStateController';
import './../styles.css';
import { ApplicationState } from "../../../store";
import { LoadingSpinner } from "../../core/LoadingSpinner";

enum UserAddNamePromptModalCurrentState {
    gettingInput,
    invalidNameSubmitted,
    contactingApi,
    apiFailed,
    apiSucceeded
}

interface Options {
    onConfirm: (name: string) => void;
}

interface AddNameState {
    currentState: UserAddNamePromptModalCurrentState,
    newName: string;
}

type UserAddNamePromptModalProps =
    ToxnotComponentProperties &
    typeof LoginStateController.actionCreators
    & Options;

/*
 A shared component to prompt the user to enter their name.
 Sets their name in the profile.
 onConfirm: A function to call after the name is set. Takes the entered name as an argument.
 */
class UserAddNamePromptModal extends React.PureComponent<UserAddNamePromptModalProps, AddNameState> {
    // ===============================================================================

    // ===============================================================================

    constructor(props: UserAddNamePromptModalProps) {
        super(props);

        this.state = {
            currentState: UserAddNamePromptModalCurrentState.gettingInput,
            newName: ""
        };
    }

    // ===============================================================================

    public render() {
        return (
            <ToxnotReactModal localizedTitle={this.props.appServices.localizeString("Set your name for your request")} modalId="user-add-name-prompt-modal">
                <ToxnotReactModalBody>
                    <p>{this.props.appServices.localizeString("You haven't set your name in your account yet.")}</p>
                    <p>{this.props.appServices.localizeString("Your request will be more likely to get a response when your name is included. We'll update your account profile, and you can change your name later in the Profile page in Settings.")}</p>

                    {this.state.currentState === UserAddNamePromptModalCurrentState.apiFailed &&
                        <div className="alert alert-warning">
                            {this.props.appServices.localizeString("An error occurred.")}
                        </div>
                    }
                    {this.state.currentState === UserAddNamePromptModalCurrentState.invalidNameSubmitted &&
                        <div className="alert alert-warning">
                            {this.props.appServices.localizeString("Please enter your name.")}
                        </div>
                    }
                    {this.state.currentState === UserAddNamePromptModalCurrentState.apiSucceeded &&
                        <div className="alert alert-success">
                            {this.props.appServices.localizeString("Your name has been updated!")}
                        </div>
                    }
                    {!(this.state.currentState === UserAddNamePromptModalCurrentState.apiSucceeded || this.state.currentState === UserAddNamePromptModalCurrentState.contactingApi) &&
                        <input type="text" className="input-group-text form-control" id="add-name-prompt-input" placeholder={this.props.appServices.localizeString("Name")} value={this.state.newName} onChange={e => this.setNameInState(e.target.value)}></input>
                    }
                    {this.state.currentState === UserAddNamePromptModalCurrentState.contactingApi &&
                        <LoadingSpinner />
                    }
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={this.props.appServices} enabledCloseButton={false}>
                    <button className="btn btn-toxnot-primary" onClick={e => this.submitButton_click()}>{this.props.appServices.localizeString("Continue")}</button>
                </ToxnotReactModalFooter>
            </ToxnotReactModal>
        );
    }

    // ===============================================================================

    private setNameInState(name: string) {
        this.setState({
            newName: name
        });
    }

    // ===============================================================================

    private submitButton_click = async () => {
        // check for valid name
        const enteredName = this.state.newName;
        if (enteredName === undefined || enteredName === "") {
            this.setState({
                currentState: UserAddNamePromptModalCurrentState.invalidNameSubmitted
            });
            return;
        }

        // contact API
        this.setState({
            currentState: UserAddNamePromptModalCurrentState.contactingApi
        });
        const serviceClient = new ToxnotServiceClient();

        // Update the user name and cookie.
        const result = await serviceClient.userUpdateProfileName(enteredName);

        // Update the react user context with the new user name.
        await this.props.getAppServices();

        if (!result.success) {
            // on failure
            this.setState({
                currentState: UserAddNamePromptModalCurrentState.apiFailed
            });
            return;
        }

        // on success
        this.setState({
            currentState: UserAddNamePromptModalCurrentState.apiSucceeded
        });
        $("#user-add-name-prompt-modal").modal("hide")
        this.props.onConfirm(enteredName);
    }
}

export default connect(
    (state: ApplicationState, ownProps: Options) => ({
        appServices: state.appServices,
        ...ownProps,
    }),
    { ...LoginStateController.actionCreators } // Selects which action creators are merged into the component's props
)(UserAddNamePromptModal as any);